import {
  Building2,
  Clock3,
  ExternalLink,
  Handshake,
  House,
  ShieldCheck,
  ShieldOff,
  Trash2,
  UserRound
} from 'lucide-react'
import { createSearchParams, Link } from 'react-router'
import { PATHS } from '@/app/router/routes'
import { CellStatusBadge } from '@/shared/components/data-table'
import { formatCode } from '@/shared/constants/config'
import { cn } from '@/shared/lib/utils'
import { useSidePanelStore } from '@/shared/store/side-panel.store'
import { Button } from '@/shared/ui/button'
import { Empty, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from '@/shared/ui/empty'
import { formatHumanDate } from '@/shared/utils/format-date'
import { grantClientRoleLabel, grantRevocationLabel } from '../models/client-account-admin-v2'
import type { ClientAccountGrantV2 } from '../services/client-accounts-admin-v2.service'

/**
 * Los accesos de una cuenta, VIVOS Y MUERTOS.
 *
 * Los revocados se muestran atenuados pero se muestran, porque «¿por qué el cliente dejó
 * de ver su inmueble?» es *la* pregunta de soporte y tres de los cinco motivos son
 * automáticos. Esconderlos convierte una revocación del sistema en un misterio que
 * termina en una llamada.
 */
type ClientAccountGrantsListProps = {
  grants: ClientAccountGrantV2[]
  canManage: boolean
  onRevoke: (grantId: string) => void
  isRevoking: boolean
}

export function ClientAccountGrantsList({
  grants,
  canManage,
  onRevoke,
  isRevoking
}: ClientAccountGrantsListProps) {
  const closePanel = useSidePanelStore((state) => state.closePanel)

  if (grants.length === 0) {
    return (
      <Empty className="py-6">
        <EmptyHeader>
          <EmptyMedia>
            <Building2 className="h-6 w-6 text-muted-foreground" aria-hidden />
          </EmptyMedia>
          <EmptyTitle>Sin inmuebles concedidos</EmptyTitle>
          <EmptyDescription>
            El cliente puede entrar a la aplicación, pero todavía no verá nada. El acceso se libera
            al aprobar la venta, o a mano desde el expediente.
          </EmptyDescription>
        </EmptyHeader>
      </Empty>
    )
  }

  return (
    <ul className="flex flex-col gap-1.5">
      {grants.map((grant) => {
        // `active` ya viene resuelto como ACCESO EFECTIVO: un grant vencido llega en `false`
        // aunque nadie lo haya revocado. Pero por qué murió cambia el renglón, y confundir las
        // dos causas es lo que deja a soporte sin respuesta.
        const inactive = !grant.active
        const revoked = Boolean(grant.revokedAt)
        const expired = inactive && !revoked
        const clientHref = grant.client.id
          ? PATHS.admin.clients.byType(grant.client.typePerson, 'view', grant.client.id)
          : null
        const propertyHref = grant.property
          ? {
              pathname: PATHS.admin.business.properties,
              search: createSearchParams({ inmueble: grant.property.code }).toString()
            }
          : null
        const AccessIcon = revoked ? ShieldOff : expired || grant.expiresAt ? Clock3 : ShieldCheck
        return (
          <li
            key={grant.id}
            className={cn(
              'overflow-hidden rounded-md border border-border bg-background',
              inactive && 'bg-muted/40'
            )}
          >
            <div className="flex items-start justify-between gap-3 border-b border-border px-3 py-2.5">
              <div className="flex min-w-0 items-start gap-2.5">
                <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
                  <Handshake className="h-4 w-4" aria-hidden />
                </span>
                <div className="min-w-0">
                  <p className="text-[10px] font-semibold tracking-wide text-muted-foreground uppercase">
                    Negociación
                  </p>
                  {grant.negotiation ? (
                    <Link
                      to={PATHS.admin.negotiations.view(grant.negotiation.id)}
                      onClick={closePanel}
                      className="inline-flex max-w-full items-center gap-1 text-sm font-semibold text-primary hover:underline"
                    >
                      <span className="truncate">
                        {formatCode(grant.negotiation.code, 'negotiation')}
                      </span>
                      <ExternalLink className="h-3.5 w-3.5 shrink-0" aria-hidden />
                    </Link>
                  ) : (
                    <p className="text-sm font-medium text-muted-foreground">
                      Negociación sin resolver
                    </p>
                  )}
                </div>
              </div>
              {grant.negotiation?.state ? (
                <CellStatusBadge
                  label={grant.negotiation.state.name || 'Sin estado'}
                  color={grant.negotiation.state.color}
                  className="mt-0.5 shrink-0"
                />
              ) : (
                <span className="mt-1 shrink-0 text-xs text-muted-foreground">Sin estado</span>
              )}
            </div>

            <div className="divide-y divide-border">
              <div className="grid grid-cols-[5.75rem_minmax(0,1fr)] items-start gap-2 px-3 py-2.5">
                <span className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
                  <House className="h-3.5 w-3.5" aria-hidden />
                  Inmueble
                </span>
                <div className="min-w-0">
                  {/* Un inmueble borrado deja el ref colgando. El acceso igual es parte de la
                      historia de la cuenta, así que la fila se muestra sin inventar inmueble. */}
                  {grant.property && propertyHref ? (
                    <Link
                      to={propertyHref}
                      onClick={closePanel}
                      className="inline-flex max-w-full items-center gap-1 text-sm font-medium hover:text-primary hover:underline"
                    >
                      <span className="truncate">{grant.property.name || grant.property.code}</span>
                      <ExternalLink className="h-3.5 w-3.5 shrink-0" aria-hidden />
                    </Link>
                  ) : (
                    <p className="text-sm font-medium text-muted-foreground">Inmueble eliminado</p>
                  )}
                  <p className="truncate text-xs text-muted-foreground">
                    {grant.property?.code ?? 'Sin código'}
                    {grant.property?.project ? ` · ${grant.property.project.name}` : ''}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-[5.75rem_minmax(0,1fr)_auto] items-start gap-2 px-3 py-2.5">
                <span className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
                  <UserRound className="h-3.5 w-3.5" aria-hidden />
                  Cliente
                </span>
                <div className="min-w-0">
                  {clientHref ? (
                    <Link
                      to={clientHref}
                      onClick={closePanel}
                      className="inline-flex max-w-full items-center gap-1 text-sm font-medium hover:text-primary hover:underline"
                    >
                      <span className="truncate">{grant.client.name || 'Cliente sin nombre'}</span>
                      <ExternalLink className="h-3.5 w-3.5 shrink-0" aria-hidden />
                    </Link>
                  ) : (
                    <p className="text-sm font-medium text-muted-foreground">
                      {grant.client.name || 'Cliente eliminado'}
                    </p>
                  )}
                  <p className="text-xs text-muted-foreground">
                    {grant.client.typePerson === 'juridical'
                      ? 'Persona jurídica'
                      : 'Persona individual'}
                  </p>
                </div>
                <span
                  className={cn(
                    'mt-0.5 rounded-full border px-2 py-0.5 text-[11px] font-medium whitespace-nowrap',
                    grant.client.principal === true
                      ? 'border-primary/20 bg-primary/10 text-primary'
                      : 'border-border bg-muted text-muted-foreground'
                  )}
                >
                  {grantClientRoleLabel(grant.client.principal)}
                </span>
              </div>
            </div>

            <div className="flex items-center justify-between gap-3 border-t border-border px-3 py-2">
              <span
                className={cn(
                  'flex min-w-0 items-center gap-1.5 text-xs',
                  inactive
                    ? 'text-muted-foreground'
                    : grant.expiresAt
                      ? 'text-amber-700 dark:text-amber-400'
                      : 'text-emerald-700 dark:text-emerald-400'
                )}
              >
                <AccessIcon className="h-3.5 w-3.5 shrink-0" aria-hidden />
                <span className="truncate">
                  {revoked
                    ? `${grantRevocationLabel(grant.revokedReason)}${
                        grant.revokedAt ? ` · ${formatHumanDate(grant.revokedAt)}` : ''
                      }`
                    : expired
                      ? `Venció el ${grant.expiresAt ? formatHumanDate(grant.expiresAt) : '—'}`
                      : grant.expiresAt
                        ? `Vence el ${formatHumanDate(grant.expiresAt)}`
                        : 'Acceso vigente'}
                </span>
              </span>

              {canManage && !inactive ? (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-7 shrink-0 px-2 text-xs text-muted-foreground hover:text-destructive"
                  disabled={isRevoking}
                  onClick={() => {
                    onRevoke(grant.id)
                  }}
                  aria-label={`Revocar el acceso a ${grant.property?.code ?? 'este inmueble'}`}
                >
                  <Trash2 className="mr-1 h-3.5 w-3.5" />
                  Revocar
                </Button>
              ) : null}
            </div>
          </li>
        )
      })}
    </ul>
  )
}
